import { autorun } from "mobx";
import { cast, flow, getEnv, types, unprotect } from "mobx-state-tree";
import { useStore } from "mobx-store-provider";
import { LegacySensor, SensorApiResponse, SensorMeta } from "../jefdaqLegacyAPI";
import { fetchPrecipAPI, PrecipApiResponse } from "../jefdaqLegacyApi/precip";
import { fetchRatedAPI, RatedApiResponse } from "../jefdaqLegacyApi/rated";
import { fetchStageAPI, StageApiResponse } from "../jefdaqLegacyApi/stage";
import { precipApiToSensors } from "../jefdaqLegacyApiToSensorModel/precip";
import { ratedApiToSensors } from "../jefdaqLegacyApiToSensorModel/rated";
import { stageApiToSensors } from "../jefdaqLegacyApiToSensorModel/stage";
import { ISensor } from "../models/Sensors";
import { isPrecipSensor } from "./PrecipSensor";
import { isStreamSensor } from "./StreamSensor";

const { maybe, frozen, model } = types;

const wait = () => new Promise((resolve) => setTimeout(resolve, 30000));

type FetchFactory = () => Promise<SensorApiResponse<SensorMeta, LegacySensor>>

export const ApiRoute = <R>(createFetch: FetchFactory) =>
  model({
    loading: false,
    response: maybe(frozen<R>()),
  })
    .volatile((self) => ({
      fetching: false,
    }))
    .actions((self) => ({
      fetch: flow(function* f() {
        if (!self.fetching) {
          self.fetching = true;
          while (self.fetching) {
            self.loading = true;
            try {
              self.response = yield createFetch();
            } catch (e) {
              console.error('Bad JEFDAQ API response', e);
            }
            self.loading = false;
            yield wait();
          }
        }
      }),
    }));

export const JefdaqLegacyAPI = model(
  {
    precip: ApiRoute<PrecipApiResponse>(fetchPrecipAPI),
    stage: ApiRoute<StageApiResponse>(fetchStageAPI),
    rated: ApiRoute<RatedApiResponse>(fetchRatedAPI),
  }).actions((self) => ({
    fetch: flow(function* f() {
      yield Promise.all([
        self.precip.fetch(),
        self.stage.fetch(),
        self.rated.fetch().catch(() => {
          console.error("Couldn't fetch rated API");
        }),
      ]);
    }),

    putSensor(sensor: ISensor) {
      const rootStore = getEnv(self).rootStore
      unprotect(rootStore);

      const sensorStore = rootStore.sensors;
      sensorStore.loading = false;
      if (isPrecipSensor(sensor)) {
        sensorStore.putSensor(sensor);
        return;
      }

      const existing = sensorStore.sensors.get(sensor.id);
      if (existing === undefined) {
        sensorStore.putSensor(sensor);
        return;
      }

      if (isStreamSensor(existing) && isStreamSensor(sensor)) {
        if (sensor.stage) {
          existing.stage = sensor.stage;
        }
        if (sensor.discharge) {
          existing.discharge = sensor.discharge;
        }
      }
    }
  })).actions((self) => ({
    afterCreate() {
      autorun(() => {
        const { precip } = self;
        if (precip.response !== undefined) {
          precipApiToSensors(precip.response).forEach((s) =>
            self.putSensor(cast(s))
          );
        }
      });

      autorun(() => {
        const { stage } = self;
        if (stage.response !== undefined) {
          stageApiToSensors(stage.response).forEach((s) =>
            self.putSensor(cast(s))
          );
        }
      });

      autorun(() => {
        const { rated } = self;
        if (rated.response !== undefined) {
          ratedApiToSensors(rated.response).forEach((s) =>
            self.putSensor(cast(s))
          );
        }
      });
    }
  }))


export const useLegacyAPIStore = () => useStore(JefdaqLegacyAPI);